import React from 'react'

import usePropertyCompareFirebaseTrigger from '@components/compare-property/utils'

import BackButton from '@pages/properties-view/apartments/v2/units-side-panel/back-button'

import {
  CloseSvg,
  CompareSvg,
  Eye3SlashSvg,
  Eye3Svg,
  FullScreenSvg,
  LinkSvg,
  PanoramaSvg,
  Pin3Svg,
  PlaySvg,
  StopCompareSvg,
  StopSvg,
  Unpin3Svg,
} from '@svg/react'

import { useCompareProperty } from '@adUtilities/components/compare-property/utils'
import { PropertyType, UnitProperty } from '@adUtilities/types'
import { Unit } from '@adUtilities/types/apartment'

interface ComponentProps {
  unit: Unit
  hasPanoramicData: boolean
  fullScreenToggle: boolean
  unitInfoToggle: boolean
  slideShowToggle: boolean
  externalLinkToggle: boolean
  showExternalLinks: boolean
  shouldShowSlideshowBtn: boolean
  isPinned: boolean
  setFullScreenToggle: (arg: boolean) => void
  setUnitInfoToggle: (arg: boolean) => void
  setSlideShowToggle: (arg: boolean) => void
  setShowExternalLinks: (arg: boolean) => void
  setShowPanoramic: () => void
  togglePin: () => void
  comparePropertyProps: ReturnType<typeof useCompareProperty>
  hideCompareAction?: boolean
  hidePropertyControls?: boolean
}

function TopControlBar({
  unit,
  hasPanoramicData,
  fullScreenToggle,
  setFullScreenToggle,
  unitInfoToggle,
  setUnitInfoToggle,
  slideShowToggle,
  setSlideShowToggle,
  externalLinkToggle,
  showExternalLinks,
  setShowExternalLinks,
  shouldShowSlideshowBtn,
  setShowPanoramic,
  isPinned,
  togglePin,
  comparePropertyProps,
  hideCompareAction,
  hidePropertyControls,
}: ComponentProps) {
  const {
    checkPropertyComparability,
    compareModeIsActive,
    setInitialPropertyAtComparisonStart,
  } = comparePropertyProps
  const { triggerComparisonStart, triggerComparisonStop } =
    usePropertyCompareFirebaseTrigger({ comparePropertyProps })

  const isComparable = React.useMemo<boolean>(
    () =>
      !hideCompareAction &&
      checkPropertyComparability({
        propertyType: PropertyType.Unit,
        data: unit,
      }),
    [unit, checkPropertyComparability, hideCompareAction]
  )

  const handleToggleCompareMode = React.useCallback(() => {
    if (!compareModeIsActive) {
      const property: UnitProperty = {
        propertyType: PropertyType.Unit,
        data: unit,
        references: {
          levelId: unit.metas.level,
        },
      }
      triggerComparisonStart({
        property,
      })
      setInitialPropertyAtComparisonStart(property)
      setFullScreenToggle(false)
      return
    }
    triggerComparisonStop()
  }, [
    compareModeIsActive,
    triggerComparisonStart,
    triggerComparisonStop,
    unit,
    setFullScreenToggle,
    setInitialPropertyAtComparisonStart,
  ])

  const handleContainerClick = React.useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      event.stopPropagation()
    },
    []
  )

  return (
    <div
      className="flex flex-col items-start gap-[14px] text-xs font-semibold tracking-widest text-white"
      onClick={handleContainerClick}
      role="none"
    >
      {fullScreenToggle && (
        <BackButton
          className="rounded bg-[#747474] p-1.5"
          iconClasses="h-5 w-5 stroke-white !scale-[1] !rotate-180"
        />
      )}

      <button
        type="button"
        onClick={() => setFullScreenToggle(!fullScreenToggle)}
        className="rounded bg-[#747474] p-1.5"
      >
        {fullScreenToggle ? (
          <CloseSvg className="h-5 w-5 !scale-[.8] text-white" />
        ) : (
          <FullScreenSvg className="h-5 w-5 text-white" />
        )}
      </button>

      {hidePropertyControls ? null : (
        <>
          {!slideShowToggle && !compareModeIsActive && (
            <button
              type="button"
              className="rounded bg-[#747474] p-1.5"
              onClick={togglePin}
            >
              {isPinned ? (
                <Unpin3Svg className="h-5 w-5" />
              ) : (
                <Pin3Svg className="h-5 w-5" />
              )}
            </button>
          )}

          {externalLinkToggle && !slideShowToggle && !compareModeIsActive && (
            <button
              type="button"
              onClick={() => setShowExternalLinks(!showExternalLinks)}
              className="rounded bg-[#747474] p-1.5"
            >
              <LinkSvg className="h-5 w-5 !scale-[.85]" />
            </button>
          )}

          {!slideShowToggle && (
            <button
              type="button"
              onClick={() => setUnitInfoToggle(!unitInfoToggle)}
              className="rounded bg-[#747474] p-1.5"
            >
              {unitInfoToggle ? (
                <Eye3SlashSvg className="h-5 w-5 stroke-white" />
              ) : (
                <Eye3Svg className="h-5 w-5 stroke-white" />
              )}
            </button>
          )}
          {isComparable && !slideShowToggle ? (
            <button
              type="button"
              className="rounded bg-[#747474] p-1.5"
              onClick={handleToggleCompareMode}
            >
              {compareModeIsActive ? (
                <StopCompareSvg
                  className="h-5 w-5 !scale-[.9] fill-white stroke-white"
                  stroke={1.6}
                />
              ) : (
                <CompareSvg
                  className="h-5 w-5 !scale-[.9] stroke-white"
                  stroke={1.6}
                />
              )}
            </button>
          ) : null}
          {hasPanoramicData && !slideShowToggle && !compareModeIsActive && (
            <button
              type="button"
              onClick={setShowPanoramic}
              className="rounded bg-[#747474] p-1.5"
            >
              <PanoramaSvg className="h-5 w-5 !scale-[.9]" />
            </button>
          )}

          {shouldShowSlideshowBtn && !compareModeIsActive && (
            <button
              type="button"
              className={`rounded bg-[#747474] ${!slideShowToggle ? 'p-2' : 'p-1.5'}`}
              onClick={() => setSlideShowToggle(!slideShowToggle)}
            >
              {!slideShowToggle ? (
                <PlaySvg className="h-4 w-4" />
              ) : (
                <StopSvg className="h-5 w-5" />
              )}
            </button>
          )}
        </>
      )}
    </div>
  )
}

export default TopControlBar
